import React, { useState } from "react";
import { Quote, Star } from "lucide-react";
import { SectionHeading, SectionLabel } from "../components/SectionHeading";
import { useSite, absoluteUrl } from "../context/SiteContext";

function ClientCell({ client }) {
  const [failed, setFailed] = useState(false);
  const src = absoluteUrl(client.logo);
  if (client.logo && !failed) {
    return (
      <img
        src={src}
        alt={client.name}
        onError={() => setFailed(true)}
        className="max-h-12 max-w-full object-contain opacity-60 hover:opacity-100 transition-opacity"
      />
    );
  }
  return (
    <span className="font-heading font-bold text-sm md:text-base tracking-widest text-[#94A3B8] text-center hover:text-[#F39C12] transition-colors">
      {client.name}
    </span>
  );
}

export default function ClientsPage() {
  const { settings, clients, testimonials } = useSite();
  const industries = settings.industries || [];

  return (
    <div data-testid="clients-page">
      <section className="border-b border-[#1F3550] relative">
        <div className="absolute inset-0 blueprint-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <SectionLabel code="C/00">Clients</SectionLabel>
          <h1 className="mt-6 font-heading font-black text-4xl md:text-6xl uppercase tracking-tight leading-[0.95] max-w-4xl">
            Trusted by India's <span className="text-[#F39C12]">heaviest industries</span>.
          </h1>
          <p className="mt-6 text-lg text-[#94A3B8] max-w-3xl leading-relaxed">
            Across steel, power, oil & gas, cement and petrochemicals — repeat orders from the most demanding industrial clients have built our reputation.
          </p>
        </div>
      </section>

      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <SectionLabel code="C/01">Selected Clients</SectionLabel>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-[#1F3550] border border-[#1F3550]">
            {clients.map((c) => (
              <div key={c.id} data-testid={`client-${c.id}`} className="bg-[#050A0F] aspect-[3/2] flex items-center justify-center px-4 hover:bg-[#0D1B2A] transition-colors">
                <ClientCell client={c} />
              </div>
            ))}
            {clients.length === 0 && <div className="col-span-full text-center text-[#94A3B8] py-12">No clients listed yet.</div>}
          </div>
        </div>
      </section>

      <section className="border-b border-[#1F3550] bg-[#0D1B2A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <SectionHeading code="C/02" overline="Client Voice" title="What our clients put on record." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.id} data-testid={`testimonial-${t.id}`} className="border border-[#1F3550] bg-[#050A0F] p-8 relative">
                <Quote className="h-7 w-7 text-[#F39C12]" />
                <p className="mt-6 text-[#F8FAFC] leading-relaxed text-sm md:text-base">"{t.text}"</p>
                <div className="mt-6 flex items-center gap-1">
                  {[...Array(t.rating || 5)].map((_, idx) => (
                    <Star key={idx} className="h-4 w-4 fill-[#F39C12] text-[#F39C12]" />
                  ))}
                </div>
                <div className="mt-5 pt-5 border-t border-[#1F3550]">
                  <div className="font-heading font-bold text-[#F8FAFC]">{t.name}</div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#94A3B8] mt-1">{t.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {industries.length > 0 && (
        <section className="border-b border-[#1F3550]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
            <SectionLabel code="C/03">Industries Served</SectionLabel>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#1F3550] border border-[#1F3550]">
              {industries.map((ind, i) => (
                <div key={ind} className="bg-[#050A0F] p-6">
                  <div className="font-mono text-[10px] tracking-[0.3em] text-[#F39C12]">I/0{i + 1}</div>
                  <div className="mt-3 font-heading font-bold text-[#F8FAFC]">{ind}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
